<template>
  <header class="navbar" :class="{ scrolled: isScrolled }">
    <div class="container navbar-inner">
      <div class="logo" @click="scrollTo('hero')">
        <img class="logo-image" src="/logo-novalab.png" :alt="$t('brand.name')" />
        <span class="logo-name">{{ $t('brand.name') }}</span>
      </div>
      <nav class="nav-links" v-show="!isMobile">
        <a
          v-for="item in navItems"
          :key="item.id"
          class="nav-link"
          :class="{ active: activeSection === item.id }"
          @click="scrollTo(item.id)">
          {{ item.label }}
        </a>
        <div class="language-switch" :aria-label="$t('nav.languageSwitch')">
          <button
            v-for="locale in locales"
            :key="locale.value"
            type="button"
            :class="{ active: $i18n.locale === locale.value }"
            @click="changeLocale(locale.value)">
            {{ locale.label }}
          </button>
        </div>
        <el-button type="primary" size="small" class="nav-cta" @click="scrollTo('contact')">{{ $t('nav.contact') }}</el-button>
      </nav>
      <div class="hamburger" v-show="isMobile" @click="mobileOpen = !mobileOpen">
        <i :class="mobileOpen ? 'el-icon-close' : 'el-icon-menu'"></i>
      </div>
    </div>
    <transition name="slide-down">
      <div class="mobile-menu" v-show="isMobile && mobileOpen">
        <a
          v-for="item in navItems"
          :key="item.id"
          class="mobile-link"
          @click="scrollTo(item.id); mobileOpen = false">
          {{ item.label }}
        </a>
        <div class="mobile-language-switch" :aria-label="$t('nav.languageSwitch')">
          <button
            v-for="locale in locales"
            :key="locale.value"
            type="button"
            :class="{ active: $i18n.locale === locale.value }"
            @click="changeLocale(locale.value)">
            {{ locale.label }}
          </button>
        </div>
        <el-button type="primary" size="small" @click="scrollTo('contact'); mobileOpen = false">{{ $t('nav.contact') }}</el-button>
      </div>
    </transition>
  </header>
</template>

<script>
export default {
  name: 'NavBar',
  data() {
    return {
      isScrolled: false,
      activeSection: 'hero',
      isMobile: false,
      mobileOpen: false
    }
  },
  computed: {
    navItems() {
      return this.$tm('nav.items')
    },
    locales() {
      return this.$tm('nav.locales')
    }
  },
  mounted() {
    window.addEventListener('scroll', this.onScroll)
    window.addEventListener('resize', this.onResize)
    this.onResize()
    this.onScroll()
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll)
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    onScroll() {
      this.isScrolled = window.scrollY > 60
      const sections = ['hero', 'about', 'business', 'product-services', 'products', 'news', 'contact']
      for (let i = sections.length - 1; i >= 0; i--) {
        const el = document.getElementById(sections[i])
        if (el && window.scrollY >= el.offsetTop - 100) {
          this.activeSection = sections[i]
          break
        }
      }
    },
    onResize() {
      this.isMobile = window.innerWidth < 900
    },
    scrollTo(id) {
      const el = document.getElementById(id)
      if (el) el.scrollIntoView({ behavior: 'smooth' })
    },
    changeLocale(locale) {
      this.$setLocale(locale)
    }
  }
}
</script>

<style lang="less" scoped>
.navbar { position: fixed; top: 0; left: 0; width: 100%; z-index: 999; transition: all .3s ease; background: transparent; padding: 16px 0; }
.navbar.scrolled { background: rgba(255,255,255,.96); backdrop-filter: blur(12px); padding: 10px 0; box-shadow: 0 2px 20px rgba(0,0,0,.08); }
.navbar.scrolled .logo-name, .navbar.scrolled .nav-link.active, .navbar.scrolled .nav-link:hover { color: #0a5c8a; }
.navbar.scrolled .nav-link, .navbar.scrolled .hamburger { color: #1a1a2e; }
.navbar.scrolled .language-switch { border-color: rgba(10,92,138,.18); background: rgba(10,92,138,.06); }
.navbar.scrolled .language-switch button { color: #1a1a2e; }
.navbar-inner { display: flex; align-items: center; justify-content: space-between; }
.logo { display: flex; align-items: center; gap: 10px; cursor: pointer; }
.logo-image { display: block; width: 82px; height: 44px; object-fit: contain; object-position: left center; }
.logo-name { font-size: 20px; font-weight: 900; color: #fff; letter-spacing: 1px; transition: color .3s; white-space: nowrap; }
.nav-links { display: flex; align-items: center; gap: 4px; }
.nav-link { padding: 6px 10px; font-size: 14px; color: rgba(255,255,255,.88); cursor: pointer; border-radius: 6px; transition: all .2s; font-weight: 500; white-space: nowrap; }
.nav-link:hover, .nav-link.active { color: #fff; background: rgba(255,255,255,.14); }
.language-switch,
.mobile-language-switch {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(255,255,255,.22);
  border-radius: 18px;
  background: rgba(255,255,255,.08);
}
.language-switch button,
.mobile-language-switch button {
  height: 24px;
  padding: 0 9px;
  border: none;
  border-radius: 14px;
  background: transparent;
  color: rgba(255,255,255,.76);
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all .2s;
}
.language-switch button.active,
.mobile-language-switch button.active {
  background: linear-gradient(135deg,#0a5c8a,#00c9a7);
  color: #fff;
}
.mobile-language-switch {
  align-self: flex-start;
  border-color: rgba(10,92,138,.18);
  background: rgba(10,92,138,.06);
  margin: 8px 16px;
}
.mobile-language-switch button {
  color: #1a1a2e;
}
.nav-cta {
  margin-left: 8px;
  height: 32px !important;
  min-width: 82px;
  display: inline-flex !important;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg,#0a5c8a,#00c9a7) !important;
  border: none !important;
  border-radius: 18px !important;
  padding: 0 16px !important;
  color: #fff !important;
  font-size: 13px !important;
  font-weight: 700 !important;
  line-height: 1 !important;
  white-space: nowrap;
  letter-spacing: 0;
}
.nav-cta /deep/ span {
  display: inline-flex;
  align-items: center;
  line-height: 1;
}
.hamburger { font-size: 24px; color: #fff; cursor: pointer; }
.mobile-menu { background: rgba(255,255,255,.98); backdrop-filter: blur(12px); padding: 16px 20px; display: flex; flex-direction: column; gap: 4px; }
.mobile-link { display: block; padding: 12px 16px; color: #1a1a2e; font-size: 15px; font-weight: 500; border-radius: 8px; cursor: pointer; }
.mobile-link:hover { background: #eef4f9; color: #0a5c8a; }
.slide-down-enter-active, .slide-down-leave-active { transition: all .3s ease; }
.slide-down-enter, .slide-down-leave-to { transform: translateY(-10px); opacity: 0; }
</style>
